/**
 * Resets all filters within the container having the class `resettable-filters-container`.
 * This method resets the values of dropdowns to 'all', ensures the 'all' checkbox is selected,
 * and unchecks all other checkboxes.
 */
function resetFilters() {
    // Reset selects
    document.querySelectorAll('.resettable-filters-container select').forEach(function (select) {
        select.value = 'all';
        select.dispatchEvent(new Event('change'));
    });
    // Reset checkboxes
    document.querySelectorAll('.resettable-filters-container input.filterCheckbox').forEach(function (checkbox) {
        if (checkbox.value === 'all' && !checkbox.checked) {
            checkbox.click();
        } else if (checkbox.value !== 'all' && checkbox.checked) {
            checkbox.click();
        }
    });
}

/**
 * Toggles the visibility of elements based on the presence of results.
 *
 * @param {boolean} show - A boolean indicating whether to show the 'noResults' section. If true, the 'noResults' element is made visible
 * and 'floorplanListBottomActions' is hidden (because "back to top" functionality is unnecessary with no results). If false, the opposite occurs.
 */
function toggleNoResults(show) {
    if (show) {
        // Show the 'noResults' section and hide the 'floorplanListBottomActions' section
        document.getElementById('noResults').classList.remove('hidden');
        document.getElementById('floorplanListBottomActions').classList.add('hidden');
    } else {
        // Hide the 'noResults' section and show the 'floorplanListBottomActions' section
        document.getElementById('noResults').classList.add('hidden');
        document.getElementById('floorplanListBottomActions').classList.remove('hidden');
    }
}

/**
 * Initialize V2 Filter logic and helper functions
 *
 * This logic is shared between Desktop and Mobile
 * Desktop specific logic should go in the `floorplan-list-filters.js` file
 * Mobile specific logic should go in the `mobile/floorplan-list-filters.js` file
 *
 */
function normalizeFloorplanListPath(pathname) {
    if (pathname.length > 1 && pathname.endsWith('/')) {
        return pathname.slice(0, -1);
    }
    return pathname;
}

function isBedroomFloorplanListPage() {
    const slugMatch = /^\/floorplans\/([^/]+)$/.exec(normalizeFloorplanListPath(window.location.pathname));
    if (!slugMatch) {
        return false;
    }
    const bedroomSlug = slugMatch[1];
    return bedroomSlug === 'studio' || bedroomSlug.indexOf('bedroom') !== -1;
}

function initFilters() {
    const filterToolbar = document.getElementById('floorplan-list-filters-container');
    const floorplanGrid = document.getElementById('floorplan-list-container');
    if (!filterToolbar || !floorplanGrid || window.RvFloorplanFilterUtils == null) {
        return;
    }

    const FLOORPLAN_LIST_CARDS_URI = '/rapi/community/floorplan-list/cards';
    const REMAINING_CARDS_PREFETCH_EVENTS = ['pointerdown', 'keydown', 'touchstart'];
    const initialBedCount = floorplanGrid.dataset.initialBedCount || '';
    let remainingCardsFetched = initialBedCount === '';
    let remainingCardsFetch = null;
    let remainingCardsPayload = null;
    let listViewSeo = typeof FLOORPLAN_LIST_VIEW_SEO !== 'undefined' ? FLOORPLAN_LIST_VIEW_SEO : null;

    function getFloorplanElements() {
        return floorplanGrid.querySelectorAll(':scope > .floorplan-list-item');
    }

    function bedCountsPresentInGrid() {
        const bedCounts = [];
        getFloorplanElements().forEach(function (floorplan) {
            const bedCount = floorplan.dataset.bedrooms || '';
            if (bedCount !== '' && bedCounts.indexOf(bedCount) === -1) {
                bedCounts.push(bedCount);
            }
        });
        return bedCounts;
    }

    function filterStateNeedsUnloadedCards(filterState) {
        if (remainingCardsFetched) {
            return false;
        }
        if (filterState.selectedBedCounts.length === 0) {
            return true;
        }
        const presentBedCounts = bedCountsPresentInGrid();
        const selectedBedMissingFromGrid = filterState.selectedBedCounts.some(function (bedCount) {
            return presentBedCounts.indexOf(bedCount) === -1;
        });
        if (selectedBedMissingFromGrid) {
            return true;
        }
        return (
            filterState.selectedUnitTypes.length > 0 ||
            filterState.selectedPetTypes.length > 0 ||
            filterState.selectedAmenities.length > 0 ||
            filterState.selectedFloorplans.length > 0 ||
            filterState.selectedPriceRange != null
        );
    }

    function bindMagnificPopups(root) {
        if (typeof jQuery === 'undefined' || typeof jQuery.fn.magnificPopup !== 'function') {
            return;
        }
        jQuery(root)
            .find('.open-popup-link-with-callbacks')
            .magnificPopup({
                type: 'inline',
                midClick: true,
                closeBtnInside: true,
                callbacks: {
                    open: function () {
                        if (this.ev.context.dataset.popupOpenCallback) {
                            const magnificPopup = jQuery.magnificPopup.instance;
                            const popupItem = magnificPopup.currItem.src;
                            window[this.ev.context.dataset.popupOpenCallback](popupItem);
                        }
                    },
                    close: function () {
                        if (this.ev.context.dataset.popupCloseCallback) {
                            const magnificPopup = jQuery.magnificPopup.instance;
                            const popupItem = magnificPopup.currItem.src;
                            window[this.ev.context.dataset.popupCloseCallback](popupItem);
                        }
                    },
                },
            });
    }

    function cardVideoAlreadyInitialized(floorplanId) {
        return (
            typeof videoPlayers !== 'undefined' &&
            videoPlayers.videos != null &&
            videoPlayers.videos[floorplanId] != null
        );
    }

    function initNewCardVideos(root) {
        root.querySelectorAll('.wistiaVideoPlayer').forEach(function (wistiaPlayer) {
            const floorplanId = wistiaPlayer.dataset.floorplanId;
            if (typeof buildWistiaVideo === 'function' && floorplanId && !cardVideoAlreadyInitialized(floorplanId)) {
                buildWistiaVideo(floorplanId);
            }
        });
        root.querySelectorAll('.youtubeVideoPlayer').forEach(function (youtubePlayer) {
            const floorplanId = youtubePlayer.dataset.floorplanId;
            const youtubeId = youtubePlayer.dataset.youtubeId;
            if (
                typeof buildYouTubeVideo === 'function' &&
                typeof YT !== 'undefined' &&
                YT.Player &&
                floorplanId &&
                youtubeId &&
                !cardVideoAlreadyInitialized(floorplanId)
            ) {
                buildYouTubeVideo(floorplanId, youtubeId);
            }
        });
    }

    function mergeFloorplanCards(cardsHtml) {
        const parser = document.createElement('div');
        parser.innerHTML = cardsHtml;
        const existingById = {};
        getFloorplanElements().forEach(function (floorplan) {
            existingById[floorplan.dataset.floorplanId] = floorplan;
        });
        parser.querySelectorAll(':scope > .floorplan-list-item').forEach(function (floorplan) {
            const floorplanId = floorplan.dataset.floorplanId;
            const existing = existingById[floorplanId];
            if (existing) {
                floorplanGrid.appendChild(existing);
                return;
            }
            floorplanGrid.appendChild(floorplan);
            bindMagnificPopups(floorplan);
            initNewCardVideos(floorplan);
        });
    }

    function bedCountForListPath(pathname) {
        const normalizedPath = normalizeFloorplanListPath(pathname);
        if (listViewSeo != null) {
            const bedroomView = (listViewSeo.bedroomViews || []).find(function (view) {
                return view.path === normalizedPath;
            });
            if (bedroomView != null && bedroomView.bedCount != null) {
                return String(bedroomView.bedCount);
            }
            return '';
        }
        return normalizedPath === normalizeFloorplanListPath(window.location.pathname) ? initialBedCount : '';
    }

    function applyListPathToBedCountFilter(pathname) {
        const pathBedCount = bedCountForListPath(pathname);
        filterToolbar
            .querySelectorAll('input[type="checkbox"][data-filter-dimension="bed-count"]')
            .forEach(function (checkbox) {
                checkbox.checked = pathBedCount !== '' && checkbox.value === pathBedCount;
            });
    }

    function bedroomViewForCurrentPath() {
        const normalizedCurrentPath = normalizeFloorplanListPath(window.location.pathname);
        return (listViewSeo?.bedroomViews ?? []).find((view) => view.path === normalizedCurrentPath) ?? null;
    }

    function seoForSelection(selectedBedCounts) {
        if (listViewSeo == null) {
            return null;
        }
        const currentBedroomView = bedroomViewForCurrentPath();
        if (
            currentBedroomView != null &&
            selectedBedCounts.length === 1 &&
            Number(selectedBedCounts[0]) === currentBedroomView.bedCount
        ) {
            return currentBedroomView;
        }
        return listViewSeo.allFloorplans;
    }

    function shouldShowLandingSeo(selectedBedCounts) {
        if (initialBedCount === '') {
            return selectedBedCounts.length === 0;
        }
        return (
            selectedBedCounts.length === 1 && selectedBedCounts[0] === initialBedCount && isBedroomFloorplanListPage()
        );
    }

    function syncHistoryAndPageCopy(filterState) {
        const viewSeo = seoForSelection(filterState.selectedBedCounts);
        if (viewSeo == null) {
            return;
        }
        if (viewSeo.path && normalizeFloorplanListPath(window.location.pathname) !== viewSeo.path) {
            history.pushState(null, '', viewSeo.path);
        }
        if (viewSeo.pageTitle) {
            document.title = viewSeo.pageTitle;
        }
        const heroHeading = document.querySelector('.hero-heading h1');
        if (heroHeading && viewSeo.heroHeading) {
            heroHeading.textContent = viewSeo.heroHeading;
        }
        const showLandingSeo = shouldShowLandingSeo(filterState.selectedBedCounts);
        document.querySelectorAll('.hero-seo-content, .floorplan-list-seo-content').forEach(function (seoBlock) {
            seoBlock.hidden = showLandingSeo === false;
        });
    }

    function fetchRemainingCards() {
        if (remainingCardsFetched) {
            return Promise.resolve(remainingCardsPayload);
        }
        if (remainingCardsPayload != null) {
            return Promise.resolve(remainingCardsPayload);
        }
        if (remainingCardsFetch != null) {
            return remainingCardsFetch;
        }
        remainingCardsFetch = fetch(FLOORPLAN_LIST_CARDS_URI, {
            credentials: 'same-origin',
            priority: 'low',
        })
            .then(function (response) {
                if (!response.ok) {
                    throw new Error('Unable to load floorplan cards');
                }
                return response.json();
            })
            .then(function (payload) {
                remainingCardsPayload = payload;
                return payload;
            })
            .catch(function () {
                remainingCardsFetch = null;
            });
        return remainingCardsFetch;
    }

    function mergeRemainingCards(payload) {
        if (remainingCardsFetched || payload == null) {
            return;
        }
        if (payload.allFloorplans != null) {
            listViewSeo = payload;
        }
        mergeFloorplanCards(payload.cardsHtml || '');
        remainingCardsFetched = true;
    }

    function ensureRemainingCards(filterState) {
        if (!filterStateNeedsUnloadedCards(filterState)) {
            return Promise.resolve();
        }
        if (remainingCardsPayload != null) {
            mergeRemainingCards(remainingCardsPayload);
            return Promise.resolve();
        }
        return fetchRemainingCards().then(function (payload) {
            mergeRemainingCards(payload);
        });
    }

    function prefetchRemainingCardsOnFirstGesture() {
        if (remainingCardsFetched) {
            return;
        }
        function prefetchOnFirstGesture() {
            REMAINING_CARDS_PREFETCH_EVENTS.forEach(function (eventName) {
                window.removeEventListener(eventName, prefetchOnFirstGesture);
            });
            fetchRemainingCards();
        }
        REMAINING_CARDS_PREFETCH_EVENTS.forEach(function (eventName) {
            window.addEventListener(eventName, prefetchOnFirstGesture, { passive: true });
        });
    }

    function applyFloorplanListFilters() {
        const filterState = window.RvFloorplanFilterUtils.getFilterStateFromToolbar(filterToolbar);
        ensureRemainingCards(filterState).then(function () {
            const currentFilterState = window.RvFloorplanFilterUtils.getFilterStateFromToolbar(filterToolbar);
            let anyFloorplanMatchesFilters = false;
            getFloorplanElements().forEach(function (floorplan) {
                const floorplanMatches = window.RvFloorplanFilterUtils.floorplanElementMatchesFilters(
                    floorplan,
                    currentFilterState,
                );
                if (floorplanMatches) {
                    anyFloorplanMatchesFilters = true;
                }
                floorplan.hidden = !floorplanMatches;
            });
            dispatchEvent(new Event('resize')); // dispatch resize event to make sure sticky footer updates correctly.
            window.RvFloorplanFilterUtils.syncActiveFilterBubbles(filterToolbar);
            if (anyFloorplanMatchesFilters) {
                document.getElementById('noResults').classList.add('hidden');
            } else {
                document.getElementById('noResults').classList.remove('hidden');
            }
            window.RvFloorplanFilterUtils.saveFiltersToSessionStorage(filterToolbar);
            syncHistoryAndPageCopy(currentFilterState);
        });
    }

    window.addEventListener('popstate', function () {
        applyListPathToBedCountFilter(window.location.pathname);
        applyFloorplanListFilters();
    });
    window.RvFloorplanFilterUtils.initFilterToolbar({
        filterToolbar: filterToolbar,
        onFiltersChanged: applyFloorplanListFilters,
        restoreFromSession: !isBedroomFloorplanListPage(),
        sessionRestoreExcludedDimensions: ['bed-count'],
    });
    prefetchRemainingCardsOnFirstGesture();
}

initFilters();
